import { useEffect, useMemo, useState } from "react";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router-dom";
import { Navigate } from "react-router-dom";

import {
  AuthProvisioningStatus,
  useGetMyProvisioningStatus,
} from "@elation/api-client/src/stitch-web";

import {
  SignupCompletionPage,
  type SignupCompletionPageState,
} from "@src/deprecated/pages/SignupCompletion";
import { PRODUCT_NAME } from "@src/shared/brand";
import { useCurrentTime } from "@src/shared/hooks/useCurrentTime";
import { navigation } from "@src/shared/navigation";
import {
  is401Error,
  is404Error,
  isHttpError,
  isNetworkError,
} from "@src/shared/utils/http/errors";
import {
  captureException,
  captureMessage,
} from "@src/shared/utils/observability/sentry";

const POLL_INTERVAL_MS = 5_000;
const NETWORK_BACKOFF_MAX_MS = 60_000;
const PENDING_TIMEOUT_MS = 10 * 60 * 1_000;
const SUPPORT_EMAIL = "support@elationemr.com";

export function meta() {
  return [
    { title: "Finishing account setup" },
    {
      name: "description",
      content: "Track your account provisioning progress",
    },
  ];
}

export function clientLoader(_: LoaderFunctionArgs) {
  return {};
}

export async function clientAction(_args: ActionFunctionArgs) {
  return {};
}

export function getPendingMessage(elapsedMs: number): string {
  if (elapsedMs < 5_000) return "Creating your account…";
  if (elapsedMs < 15_000) return "Setting up your practice in Elation…";
  if (elapsedMs < 30_000) return "Almost ready — finalizing your account…";
  return "This is taking longer than usual. Hang tight.";
}

export function getNetworkBackoffDelayMs(consecutiveFailures: number): number {
  if (consecutiveFailures <= 0) return POLL_INTERVAL_MS;

  return Math.min(
    POLL_INTERVAL_MS * 2 ** (consecutiveFailures - 1),
    NETWORK_BACKOFF_MAX_MS,
  );
}

function buildSupportHref(requestId?: string): string {
  const params = new URLSearchParams();
  params.set("subject", `${PRODUCT_NAME.singular} account provisioning issue`);
  params.set(
    "body",
    requestId
      ? `Reference: ${requestId}`
      : "I need help finishing account setup.",
  );

  return `mailto:${SUPPORT_EMAIL}?${params.toString()}`;
}

type ProvisioningStatusPollingState = {
  data?: { data?: AuthProvisioningStatus };
  error: unknown;
  fetchFailureCount: number;
  pendingElapsedMs?: number;
};

export function getProvisioningStatusRefetchInterval({
  data,
  error,
  fetchFailureCount,
  pendingElapsedMs = 0,
}: ProvisioningStatusPollingState): number | false {
  const latestData = data?.data;

  if (latestData?.state === "succeeded" || latestData?.state === "failed") {
    return false;
  }

  if (pendingElapsedMs >= PENDING_TIMEOUT_MS) {
    return false;
  }

  if (error) {
    if (is404Error(error)) {
      return false;
    }

    if (isNetworkError(error)) {
      return getNetworkBackoffDelayMs(fetchFailureCount);
    }

    return false;
  }

  return POLL_INTERVAL_MS;
}

export function getFailureReason(status: AuthProvisioningStatus) {
  const failureCode = status.failureReason?.code;

  if (failureCode === "publish_failed") {
    return {
      title: "Account setup failed",
      description:
        "Something went wrong while setting up your account. Please contact support and reference the ID below.",
    };
  }

  if (failureCode === "user_already_exists") {
    return {
      title: "This email is already registered with Elation.",
      description: "Sign in instead or use a different email.",
      signInHref: navigation.login(),
    };
  }

  if (failureCode === "validation_error") {
    return {
      title: "Account setup failed",
      description:
        "We couldn't validate your information. Please contact support and reference the ID below.",
    };
  }

  return {
    title: "Account setup failed",
    description:
      "Something went wrong while setting up your account. Please contact support and reference the ID below.",
  };
}

function getFailedView(status: AuthProvisioningStatus, onRetry: () => void) {
  const requestId = status.requestId;

  return {
    state: "failed",
    requestId,
    supportHref: buildSupportHref(requestId),
    onRetry,
    ...getFailureReason(status),
  } as const;
}

function getTimedOutView(
  requestId: string | undefined,
  onRetry: () => void,
): SignupCompletionPageState {
  return {
    state: "failed",
    title: "Account setup is taking unusually long",
    description: requestId
      ? "Please contact support and reference the ID below."
      : "Please contact support for help finishing account setup.",
    requestId,
    supportHref: buildSupportHref(requestId),
    onRetry,
  };
}

function getPendingView(nowMs: number, pendingStartedAt: number) {
  return {
    state: "pending",
    message: getPendingMessage(nowMs - pendingStartedAt),
  } as const;
}

function getErrorReference(error: unknown): string | undefined {
  if (!isHttpError(error)) return undefined;

  const data = error.response.data;
  if (data && typeof data === "object") {
    for (const key of ["requestId", "traceId", "errorId"] as const) {
      const value = (data as Record<string, unknown>)[key];
      if (typeof value === "string" && value.length > 0) {
        return value;
      }
    }
  }

  const headers = error.response.headers as Record<string, unknown> | undefined;
  const headerValue = headers?.["x-request-id"] ?? headers?.["x-trace-id"];
  return typeof headerValue === "string" && headerValue.length > 0
    ? headerValue
    : undefined;
}

function getUnexpectedErrorView(
  error: unknown,
  onRetry: () => void,
): SignupCompletionPageState {
  const requestId = getErrorReference(error);

  return {
    state: "failed",
    title: "Account setup failed",
    description: requestId
      ? "Something went wrong while setting up your account. Please contact support and reference the ID below."
      : "Something went wrong while setting up your account. Please contact support for help.",
    requestId,
    supportHref: buildSupportHref(requestId),
    onRetry,
  };
}

export default function SignupCompletionRoute() {
  const [pendingStartedAt] = useState(() => Date.now());

  const nowMs = useCurrentTime();
  const pendingElapsedMs = nowMs - pendingStartedAt;

  const { data, error, refetch } = useGetMyProvisioningStatus({
    query: {
      enabled: true,
      retry: false,
      refetchInterval: (query) => {
        return getProvisioningStatusRefetchInterval({
          data: query.state.data as { data?: AuthProvisioningStatus },
          error: query.state.error,
          fetchFailureCount: query.state.fetchFailureCount,
          pendingElapsedMs: Date.now() - pendingStartedAt,
        });
      },
    },
  });

  const status = data?.data;

  useEffect(() => {
    if (status?.state !== "failed") return;

    console.error("[signup-completion] account provisioning failed", {
      requestId: status.requestId,
      failureReason: status.failureReason,
    });
    captureMessage("[signup-completion] account provisioning failed", {
      level: "error",
      extra: {
        requestId: status.requestId,
        failureReason: status.failureReason,
      },
    });
  }, [status]);

  useEffect(() => {
    if (
      !error ||
      isNetworkError(error) ||
      is404Error(error) ||
      is401Error(error)
    ) {
      return;
    }

    console.error(
      "[signup-completion] unexpected error fetching provisioning status",
      error,
    );
    captureException(error, {
      tags: {
        route: "signup-completion",
        operation: "getMyProvisioningStatus",
      },
    });
  }, [error]);

  const view = useMemo<SignupCompletionPageState>(() => {
    if (status?.state === "failed") {
      return getFailedView(status, () => void refetch());
    }

    if (status?.state === "pending" && pendingElapsedMs >= PENDING_TIMEOUT_MS) {
      return getTimedOutView(status.requestId, () => void refetch());
    }

    if (!status && pendingElapsedMs >= PENDING_TIMEOUT_MS) {
      return getTimedOutView(undefined, () => void refetch());
    }

    if (
      error &&
      !isNetworkError(error) &&
      !is404Error(error) &&
      !is401Error(error)
    ) {
      return getUnexpectedErrorView(error, () => void refetch());
    }

    return getPendingView(nowMs, pendingStartedAt);
  }, [status, error, nowMs, pendingStartedAt, pendingElapsedMs, refetch]);

  if (status?.state === "succeeded") {
    return <Navigate to={navigation.dashboard()} replace />;
  }

  if (is404Error(error)) {
    return <Navigate to={navigation.dashboard()} replace />;
  }

  if (is401Error(error)) {
    return <Navigate to={navigation.login()} replace />;
  }

  return <SignupCompletionPage view={view} />;
}
